import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { useForm, Controller, SubmitHandler } from 'react-hook-form-latest';
import { useRouteParams } from 'utils/useIdParam';
import { ROUTES } from 'config';
import { SupplierStatus } from 'store/suppliers';
import {
  useAdminSupplier,
  useAdminUpdateSupplier,
} from 'store/admin-suppliers';
import { usePredefinedToasts } from 'utils/Toast';
import {
  Button,
  DataLossPrompt,
  FileInput,
  Footer,
  ImageList,
  Panel,
  SwitchInput,
  TextInput,
} from 'melp-design/components';
import { useUploadFile } from 'store/files';
import { Delete } from '@mui/icons-material';

interface Values {
  name: string;
  status: SupplierStatus;
  logo: string | null;
  description: string;
  additionalInfo: string;
  termsAndConditionsLink: string;
}

const INITIAL_VALUES: Values = {
  name: '',
  status: 'inactive',
  logo: null,
  description: '',
  additionalInfo: '',
  termsAndConditionsLink: '',
};

export const AdminSupplierDetails: FC = () => {
  const { t } = useTranslation();
  const { id: supplierId } = useRouteParams(ROUTES.admin.suppliers.details);
  const predefinedToasts = usePredefinedToasts();

  const { data: supplier } = useAdminSupplier(supplierId);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  useEffect(() => {
    if (supplier) {
      reset({
        name: supplier.name,
        status: supplier.status,
        logo: supplier.logo,
        description: supplier.description,
        additionalInfo: supplier.additionalInfo,
        termsAndConditionsLink: supplier.termsAndConditionsLink,
      });
    }
  }, [supplier, reset]);

  const { mutate: updateSupplier, isLoading: isUpdating } =
    useAdminUpdateSupplier();
  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateSupplier(
      { supplierId, data: values },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
        },
      },
    );
  };

  if (!supplier) return null;

  const [logo] = watch(['logo']);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Panel title={t('common.settings')}>
            <Stack spacing={2}>
              <Controller
                name="status"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SwitchInput
                    label={t('status.active')}
                    name={name}
                    value={value === 'active'}
                    onChange={(checked) =>
                      onChange(checked ? 'active' : 'inactive')
                    }
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="name"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextInput
                    label={t('marketplaceSuppliers.name')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    required
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="logo"
                control={control}
                render={({ field: { name, onChange, ref } }) => {
                  return logo ? (
                    <ImageList
                      items={[
                        {
                          imageId: logo,
                          actions: [
                            {
                              icon: Delete,
                              variant: 'neutral-outline',
                              onClick: () => onChange(''),
                            },
                          ],
                        },
                      ]}
                    />
                  ) : (
                    <FileInput
                      name={name}
                      onChange={([file]) => {
                        if (file) {
                          uploadFile(
                            { file },
                            {
                              onSuccess: ({ data }) => {
                                onChange(data.id);
                              },
                            },
                          );
                        } else {
                          onChange('');
                        }
                      }}
                      disabled={isUploading}
                      maxFiles={1}
                      previewSelectedFiles={false}
                      accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="description"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextInput
                    label={t('employees.details')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    rows={3}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="additionalInfo"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextInput
                    label={t('marketplaceSuppliers.additionalInfo')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    rows={3}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="termsAndConditionsLink"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextInput
                    label={t('marketplaceSuppliers.termsAndConditionsLink')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    ref={ref}
                  />
                )}
              />
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap="10px">
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              type="submit"
              disabled={isUpdating}
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={isDirty && !isUpdating} />
    </>
  );
};
